import { buildSsoRedirectUrl, DEFAULT_SSO_START_URL } from "@axisnode/auth-client";
import type { ReactNode } from "react";
import { useSession } from "./session";

export type AuthGateProps = {
  children: ReactNode;
  /** Shown while the session probe is in flight. */
  loadingFallback?: ReactNode;
  /**
   * When true (default), anonymous visitors are sent to SSO.
   * Set false only for public chrome that still wants session context.
   */
  requireAuth?: boolean;
  /** Override return URL for SSO `rd`. Default: current location. */
  returnUrl?: string;
  ssoStartUrl?: string;
  /** Injected navigation (tests). Default: location.assign. */
  assignLocation?: (url: string) => void;
};

/**
 * Auth gate — Platform chrome only. Blocks anonymous access by starting the
 * same oauth2-proxy round-trip the three legacy apps already use.
 * Never interprets Native-System authorization (roles/capabilities).
 */
export function AuthGate({
  children,
  loadingFallback = <div data-testid="shell-auth-loading">Loading session…</div>,
  requireAuth = true,
  returnUrl,
  ssoStartUrl = DEFAULT_SSO_START_URL,
  assignLocation,
}: AuthGateProps) {
  const { session } = useSession();

  if (session.status === "loading") {
    return <>{loadingFallback}</>;
  }

  if (requireAuth && session.status === "anonymous") {
    const href =
      returnUrl ??
      (typeof globalThis.location !== "undefined"
        ? globalThis.location.href
        : "");
    const target = buildSsoRedirectUrl(href, ssoStartUrl);
    if (assignLocation) {
      assignLocation(target);
    } else if (typeof globalThis.location !== "undefined") {
      globalThis.location.assign(target);
    }
    return (
      <div data-testid="shell-auth-redirecting">Redirecting to sign-in…</div>
    );
  }

  return <>{children}</>;
}
